import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  differenceInDays,
  differenceInMonths,
  isSameMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  endOfDay,
  format,
  formatISO,
  isBefore,
  startOfDay,
  subDays,
} from 'date-fns';
import Report from '../../../../componentsV2/Layout/Report';
import useSearchParams from '../../../../hooks/useSearchParams';
import { useReport } from '../../../../context/ReportContext';
import statusOptions from './resources/statusOptions.json';
import { filterList, filterStatList } from './helpers/filterList';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';
import AppointmentsExpandRow from './components/AppointmentsExpandRow';


const colorMap = {
  COMPLETED: { backgroundColor: '#ECF8DD', color: '#7FBD31' },
  CANCELED: { backgroundColor: '#f9e0e0', color: '#ff7a7a' },
  NO_SHOW: { backgroundColor: '#f9e0e0', color: '#ff7a7a' },
  REQUESTED: { backgroundColor: '#e2e3e5', color: '#383d41' },
  ASSIGNED: { backgroundColor: '#e0f2ff', color: '#007bd0' },
};


function getAssociateName(stat) {
  if (stat.associate_first_name && stat.associate_last_name) {
    return `${stat.associate_first_name} ${stat.associate_last_name}`;
  }
  if (stat.associate_first_name) {
    return stat.associate_first_name;
  }
  if (stat.associate_last_name) {
    return stat.associate_last_name;
  }
  return 'Unassigned';
}

const Appointments = ({ auth }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSizeTable, setPageSizeTable] = useState(10);
  const [page, setPage] = useState(1);
  const { setValue: setSearchParams, getAll } = useSearchParams();
  const { appointments, isLoadingAppointments } = useReport();
  const [dateRange, setDateRange] = useState([subDays(new Date(), 90), new Date()]);

  const [filters, setFilters] = useState({
    status: [],
    store: [],
    associate: [],
    source: [],
  });

  const [appointmentType, setAppointmentType] = useState({
    /*typeId: name */
  });

  const params = getAll();

  const options = useMemo(() => statusOptions, []);

  useEffect(() => {
    const paramsFilters = {
      status: params?.status ? params?.status : [],
      store: params?.store ? params?.store : [],
      associate: params?.associate ? params?.associate : [],
      source: params?.source ? params?.source : [],
    };
    setFilters(paramsFilters);
    if (params?.startDate && params?.endDate) {
      setDateRange([new Date(params.startDate), new Date(params.endDate)]);
    } else {
      setDateRange([startOfDay(subDays(new Date(), 90)), endOfDay(new Date())]);
    }
  }, [window.location.search]);

  const getDefaultPeriod = useMemo(() => {
    if (params?.startDate && params?.endDate) {
      if (
        isSameMonth(new Date(params.endDate), new Date(params.startDate)) &&
        isFirstDayOfMonth(new Date(params.startDate)) &&
        isLastDayOfMonth(new Date(params.endDate)) &&
        differenceInMonths(new Date(), new Date(params.startDate)) === 1
      ) {
        return 'previousmonth';
      }
      return `${format(new Date(params.startDate), 'MM/dd/yyyy')} - ${format(new Date(params.endDate), 'MM/dd/yyyy')}`;
    }
    return 'previousmonth';
  }, []);

  const handleOnDateRangeChange = useCallback(dates => {
    setSearchParams('startDate', formatISO(dates[0]));
    setSearchParams('endDate', formatISO(dates[1]));
  }, []);

  const reportData = useMemo(() => {
    const appointmentMap = new Map();

    // First, add all stats data (all appointments with status info)
    appointments?.stats?.forEach(stat => {
      const statObj = {
        client_id: stat.client_id,
        client_name: `${stat.client_name}` || `Client ${stat.client_id}`,
        activity_store_id: stat.store_id,
        activity_store_name: `${stat.store_name}` || `Store ${stat.store_id}`,
        activity_associate_id: stat.associate_id,
        activity_associate_name: getAssociateName(stat),
        appointment_id: stat.appointment_id,
        appointment_type_id: stat.appointment_type_id,
        appointment_type_name: stat.appointment_type_name,
        activity_date: stat.appointment_date,
        formattedActivityDate: stat.appointment_date && format(new Date(stat.appointment_date), 'yyyy-MM-dd'),
        status_code: stat.status_code,
        status_name: stat.status_name,
        activity_source: stat.appointment_source || '',
        appointment_created_date: stat.appointment_created_date,
        formattedCreatedDate: stat.appointment_created_date && format(new Date(stat.appointment_created_date), 'yyyy-MM-dd'),
      };

      // Only add if we don't have this appointment yet, or update if this is newer
      if (!appointmentMap.has(stat.appointment_id)) {
        appointmentMap.set(stat.appointment_id, statObj);
      }
    });

    // Then merge with reportData (appointments with sales transactions)
    appointments?.reportData?.forEach(sale => {
      const clientObj = { ...sale };
      if (!clientObj.activity_source) {
        clientObj.activity_source = '';
      }
      appointmentMap.set(sale.appointment_id, clientObj);
    });

    return Array.from(appointmentMap.values());
  }, [appointments]);

  const filteredData = useMemo(() => {
    const sources = [];
    setPage(1);
    return filterList({
      appointments: reportData.map(appointment => {
        return {
          ...appointment,
        };
      }),
      filters,
      sources,
      startDate: dateRange[0],
      endDate: dateRange[1],
      assocStores: auth.stores,
    });
  }, [reportData, filters, dateRange]);

  const filteredStats = useMemo(() => {
    const sources = [];
    setPage(1);
    return filterStatList({
      appointments: appointments?.stats || [],
      filters,
      sources,
      startDate: dateRange[0],
      endDate: dateRange[1],
      assocStores: auth.stores,
    });
  }, [appointments?.stats, filters, dateRange]);

  const dataBySales = useMemo(() => {
    const data = new Map();
    filteredData.forEach(item => {
      if (!data.has(item.sales_transaction_id)) {
        data.set(item.sales_transaction_id, item);
      }
    });
    return Array.from(data.values());
  }, [filteredData]);

  const dataByClientId = useMemo(() => {
    const data = new Map();
    filteredData.forEach(item => {
      if(data.has(item.appointment_id)) {
        const existingData = data.get(item.appointment_id);
        existingData.purchases = existingData.purchases + (item.sales_transaction_id ? 1 : 0);
        existingData.related_sales =
          existingData.related_sales + (parseFloat(item.sales_transaction_amount) || 0);
        if (item.sales_transaction_id) {
          existingData.transactions.push({
            sales_transaction_amount: item.sales_transaction_amount,
            sales_transaction_id: item.sales_transaction_id,
            sales_transaction_date: item.sales_transaction_date,
            client_id: item.client_id,
            client_name: item.client_name,
          });
        }
        data.set(item.appointment_id, existingData);
      } else {
        const newData = {
          ...item,
          appointment_name: appointmentType[item.appointment_type_id],
          purchases: item.sales_transaction_id ? 1 : 0,
          related_sales: parseFloat(item.sales_transaction_amount) || 0,
          transactions: item.sales_transaction_id
            ? [
              {
                sales_transaction_amount: item.sales_transaction_amount,
                sales_transaction_id: item.sales_transaction_id,
                sales_transaction_date: item.sales_transaction_date,
                client_id: item.client_id,
                client_name: item.client_name,
              },
            ]
            : [],
        };
        delete newData.sales_transaction_amount;
        // delete newData.sales_transaction_id; // I dunno
        delete newData.sales_transaction_date;

        data.set(item.appointment_id, newData);
      }
    });
    return Array.from(data.values());
  }, [filteredData]);

  const removeFilter = useCallback(
    (key, value) => {
      const updatedFilters = { ...filters };
      updatedFilters[key] = updatedFilters[key].filter(filter => filter !== value);
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  let statsObj = {};

  // Filter reportData for Related Sales calculation
  const filteredReportDataForSales = useMemo(() => {
    if (!appointments?.reportData) return [];

    return appointments.reportData.filter(item => {
      // Date filter
      const itemDate = new Date(item.sales_transaction_date || item.activity_date);
      const isInDateRange = itemDate >= dateRange[0] && itemDate <= dateRange[1];

      // Store filter
      const storeMatch =
        filters.store.length === 0 ||
        filters.store.includes(item.activity_store_id?.toString()) ||
        filters.store.includes(item.store_id?.toString());

      // Associate filter
      const associateMatch =
        filters.associate.length === 0 || filters.associate.includes(item.activity_associate_id?.toString());

      return isInDateRange && storeMatch && associateMatch;
    });
  }, [appointments?.reportData, dateRange, filters]);

  const stats = useMemo(() => {
    let statStores = {
      REQUESTED: {
        stores: {},
        count: 0,
      },
      ASSIGNED: {
        stores: {},
        count: 0,
      },
      COMPLETED: {
        stores: {},
        count: 0,
      },
      CANCELED: {
        stores: {},
        count: 0,
      },
      NO_SHOW: {
        stores: {},
        count: 0,
      },
    };

    let statSources = {
      ASSOCIATE: 0,
      CLIENT: 0
    }

    filteredStats?.forEach(stat => {
      statsObj[stat.appointment_type_id] = stat.appointment_type_name;
      if (statStores[stat.status_code]) {
        statStores[stat.status_code].count += 1;
        statStores[stat.status_code].stores[stat.store_id] = stat.store_id;
      }
      if(stat.appointment_source) {
        statSources[stat.appointment_source] = (statSources[stat.appointment_source] || 0) + 1;
      }
    });
    setAppointmentType(statsObj);
    const totalCount = statStores['REQUESTED'].count + statStores['ASSIGNED'].count + statStores['COMPLETED'].count + statStores['CANCELED'].count + statStores['NO_SHOW'].count;
    const totalStores = Object.keys({
      ...statStores['COMPLETED'].stores,
      ...statStores['CANCELED'].stores,
      ...statStores['NO_SHOW'].stores,
    }).length;
    const canceledNoShowCount = statStores['CANCELED'].count + statStores['NO_SHOW'].count;
    const canceledNoShowStores = Object.keys({
      ...statStores['CANCELED'].stores,
      ...statStores['NO_SHOW'].stores,
    }).length;

    const totalSales = filteredReportDataForSales
      .reduce((acc, item) => {
        if (item.sales_transaction_amount) {
          return acc + parseFloat(item.sales_transaction_amount);
        }
        return acc;
      }, 0)
      .toFixed(2);

    const completePercentage = totalCount ? ((statStores['COMPLETED'].count / totalCount) * 100).toFixed(2) : 0;
    const canceledPercentage = (100 - completePercentage).toFixed(2);

    return [
      {
        title: 'All',
        value: totalCount || 0,
        description: <span className="fs-14 mid-gray m-top-3">{statSources.CLIENT || 0} Kiosk, {statSources.ASSOCIATE || 0} Added Manually</span>,
      },
      {
        title: 'Completed',
        value: statStores['COMPLETED'].count || 0,
        description: (
          <div className="fs-14 mid-gray m-top-3" style={{ height: '23px' }}>
            Across {Object.keys(statStores['COMPLETED'].stores).length || 0} stores
          </div>
        ),
      },
      {
        title: 'Not Completed',
        value: canceledNoShowCount || 0,
        description: <span className="fs-14 mid-gray m-top-3">{statStores['CANCELED'].count || 0} Canceled, {statStores['NO_SHOW'].count || 0} No Show</span>,
      },
      {
        title: 'Related Sales',
        value: formatNumberAsCurrency(totalSales),
        description: (
          <span className="fs-14 mid-gray m-top-3">{`Across ${filteredReportDataForSales.length || 0
            } transactions`}</span>
        ),
        hidden: !auth?.pos_type,
      },
    ];
  }, [dataByClientId, dataBySales, auth?.pos_type]);

  const graphData = useMemo(() => {
    const data = {
      REQUESTED: [],
      ASSIGNED: [],
      CANCELED: [],
      NO_SHOW: [],
      COMPLETED: [],
    };
    filteredStats.forEach(stat => {
      if (data[stat.status_code]) {
        data[stat.status_code].push({
          total: 1,
          date: stat.appointment_date,
        });
      }
    });

    // Format keys for chart display
    return {
      'Requested': data.REQUESTED,
      'Assigned': data.ASSIGNED,
      'Canceled': data.CANCELED,
      'No Show': data.NO_SHOW,
      'Completed': data.COMPLETED,
    };
  }, [filteredStats]);

  const columns = useMemo(() => {
    const relatedSales = auth?.pos_type
      ? [
        {
          title: 'Related Sales',
          dataIndex: 'related_sales',
          key: 'related_sales',
          sorter: (a, b) => a.related_sales - b.related_sales,
          render: amount => (amount ? formatNumberAsCurrency(amount) : '-'),
        },
      ]
      : [];
    return [
      {
        title: 'Created Date',
        dataIndex: 'appointment_created_date',
        key: 'appointment_created_date',
        key_export: 'formattedCreatedDate',
        sorter: (a, b) => {
          const dateA = new Date(a.appointment_created_date);
          const dateB = new Date(b.appointment_created_date);

          return dateA - dateB;
        },
        defaultSortOrder: 'descend',
        render: value => value ? format(new Date(value), 'MMM dd, yyyy') : '-',
      },
      {
        title: 'Appointment Date',
        dataIndex: 'activity_date',
        key: 'activity_date',
        key_export: 'formattedActivityDate',
        sorter: (a, b) => {
          const dateA = new Date(a.activity_date);
          const dateB = new Date(b.activity_date);

          return dateA - dateB;
        },
        render: value => format(new Date(value), 'MMM dd, yyyy'),
      },
      {
        title: 'Client',
        dataIndex: 'client_name',
        key: 'client_name',
        sorter: (a, b) => a.client_name.localeCompare(b.client_name),
        render: (text, record) => (
          <a href={`/Clients?client=${record.client_id}`} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ),
      },
      {
        title: 'Associate',
        dataIndex: 'activity_associate_name',
        key: 'activity_associate_name',
        sorter: (a, b) => {
          const textA = a?.activity_associate_name?.toUpperCase() || 'zzzzzzzz';
          const textB = b?.activity_associate_name?.toUpperCase() || 'zzzzzzzz';
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        },
      },
      {
        title: 'Store',
        dataIndex: 'activity_store_name',
        key: 'activity_store_name',
        sorter: (a, b) => a.activity_store_name.localeCompare(b.activity_store_name),
      },
      {
        title: 'Reason',
        dataIndex: 'appointment_type_name',
        key: 'appointment_type_name',
      },
      {
        title: 'Source',
        dataIndex: 'activity_source',
        key: 'activity_source',
        render: (text, record) => {
          let textField = '';
          if(text === 'ASSOCIATE') {
            textField = 'Added Manually';
          } else if(text === 'CLIENT') {
            textField = 'Kiosk';
          } else {
            textField = '';
          }
          return (
          <span>{textField}</span>
        )},
      },
      {
        title: 'Status',
        dataIndex: 'status_name',
        key: 'status_name',
        render: (t, obj) => {
          const color = colorMap[obj.status_code];
          return (
            <span
              className="badge"
              style={{
                ...color,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 500,
              }}
            >
              {t}
            </span>
          );
        },
      },
      ...relatedSales,
    ];
  }, [auth?.pos_type]);

  const handleExpandRow = useCallback(
    (expand, record) => {
      let updatedExpandedRowKeys = [...expandedRowKeys];
      if (expand) {
        updatedExpandedRowKeys.push(record.client_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      } else {
        updatedExpandedRowKeys = updatedExpandedRowKeys.filter(key => key !== record.client_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      }
    },
    [expandedRowKeys],
  );

  const expandedRowRender = useCallback(record => {
    const appointment_name = appointmentType[record.appointment_type_id] || '';
    return <AppointmentsExpandRow record={{ ...record, appointment_name }} licenseKey={auth.licenseKey} />;
  }, []);

  const handleFilterChange = useCallback(
    value => {
      const updatedFilters = {
        status: [],
        store: [],
        associate: [],
        source: [],
      };
      value.forEach(item => {
        if (item[0] && item[1]) {
          updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
        } else {
          updatedFilters[item[0]] = [];
        }
      });
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  return (
    <Report
      title="Appointments"
      stats={stats}
      options={options}
      hasCSVExport
      CSVFilename="appointments.csv"
      handleFilterChange={handleFilterChange}
      filters={filters}
      removeFilter={removeFilter}
      onDateRangeChange={handleOnDateRangeChange}
      dataRange={dateRange}
      defaultPeriodValue={getDefaultPeriod}
      graphData={graphData}
      maxStatsPerLine={4}
      stacked
      columns={columns}
      table={dataByClientId}
      hasBanner={isBefore(dateRange[0], new Date('2024-04-25'))}
      textBanner="Clientbook started to track that information as of April 25th, 2024. You are unable to see the information before that date."
      tablesProps={{
        pagination: {
          total: dataByClientId.length,
          showSizeChanger: true,
          showTotal: total => `Total ${total} items`,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSizeTable(pageSize);
          },
          page,
          pageSize: pageSizeTable,
        },
        rowKey: 'appointment_id',
        expandable: {
          expandedRowRender: record => expandedRowRender(record),
          defaultExpandedRowKeys: ['0'],
          onExpand: (expanded, record) => handleExpandRow(expanded, record),
          expandedRowKeys,
          rowExpandable: record => true,
          expandRowByClick: true,
        },
      }}
    />
  );
};
const mapStateToProps = state => ({
  auth: state.auth,
});
export default connect(mapStateToProps)(Appointments);
